<template>
  <focus-trap :clickDeactivates="false" :escapeDeactivates="false">
    <div
      class="idexx-measurement-result-popup"
      :tabindex="`-${layerNumber}`"
      @keyup.esc.stop="handleClose"
    >
      <validation-observer v-slot="{ invalid }">
        <popup
          :buttons="['閉じる', '取込']"
          :content-style="{
            maxHeight: '650px',
            marginTop: '25px',
            padding: '0 30px'
          }"
          :invalid="isRegistrationDisabled(invalid)"
          :layer-number="layerNumber"
          @cancel="handleClose"
          @click-close-mark="handleClose"
          @decision="checkSpeciesThenCreate"
        >
          <template v-slot:content>
            <div class="content">
              <div class="header">
                <div class="title">
                  <span>{{ 'IDEXX検査結果 取込' }}</span>
                </div>
              </div>
              <div class="input-form">
                <div class="file-input">
                  <base-file-input
                    data-test="base-file-input"
                    :accept="'.xml'"
                    :disabled="isFileChanging"
                    :input-style="{ height: '60px' }"
                    :label="fileInputLabel"
                    @input="inputFile"
                  />
                  <div
                    v-if="fileName"
                    class="file-detail"
                    data-test="file-detail"
                  >
                    <div class="file-name">
                      <div class="icon">
                        <img
                          class="check"
                          alt="ファイル読み込み完了アイコン"
                          src="@/assets/images/check_16px.png"
                          width="16px"
                          height="16px"
                        />
                      </div>
                      <div class="text">
                        <span>{{ fileName }}</span>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="patient">
                  <measurement-result-patient-detail
                    data-test="measurement-result-patient-detail"
                    :patient="patient"
                    :run-date="runDate"
                  />
                </div>
                <div class="measurement-result">
                  <measurement-result-input-table
                    v-if="showInputTable"
                    data-test="measurement-result-input-table"
                    :measurement-result="measurementResult"
                    :measurement-result-items="measurementResultItems"
                    :patient="patient"
                    @input-measurement-result="inputMeasurementResult"
                    @input-measurement-result-item="inputMeasurementResultItem"
                    @input-measurement-result-items="
                      inputMeasurementResultItems
                    "
                  />
                </div>
              </div>
            </div>
          </template>
        </popup>
      </validation-observer>
      <announce-popup
        v-if="alertPopup.isOpened"
        data-test="announce-popup alert"
        :buttons="alertPopup.buttons"
        :layer-number="alertPopup.layerNumber"
        :left-align-message="alertPopup.leftAlignMessage"
        :title="alertPopup.title"
        :type="alertPopup.type"
        @close="closeAlertPopup"
        >{{ alertPopup.message }}</announce-popup
      >
      <announce-popup
        v-if="confirmationPopup.isOpened"
        data-test="announce-popup confirmation"
        :buttons="confirmationPopup.buttons"
        :layer-number="confirmationPopup.layerNumber"
        :title="confirmationPopup.title"
        :type="confirmationPopup.type"
        @cancel="closeConfirmationPopup"
        @decision="closeSelf"
        >{{ confirmationPopup.message }}</announce-popup
      >
      <announce-popup
        v-if="resultPopup.isOpened"
        data-test="announce-popup result"
        :buttons="resultPopup.buttons"
        :layer-number="resultPopup.layerNumber"
        :title="resultPopup.title"
        :type="resultPopup.type"
        @close="closeResultPopup"
        >{{ resultPopup.message }}</announce-popup
      >
      <announce-popup
        v-if="speciesAlertPopup.isOpened"
        data-test="announce-popup species-alert"
        :buttons="speciesAlertPopup.buttons"
        :disabled="isCreatingMeasurement"
        :layer-number="speciesAlertPopup.layerNumber"
        :title="speciesAlertPopup.title"
        :type="speciesAlertPopup.type"
        @cancel="closeSpeciesAlertPopup"
        @decision="createMeasurement"
        >{{ speciesAlertPopup.message }}</announce-popup
      >
    </div>
  </focus-trap>
</template>

<script>
import AnnouncePopup from '@/components/popups/AnnouncePopup'
import BaseFileInput from '@/components/parts/atoms/BaseFileInput'
import CheckPopupInputDifference from '@/components/mixins/CheckPopupInputDifference'
import MeasurementResultInputTable from '@/components/parts/organisms/MeasurementResultInputTable'
import MeasurementResultPatientDetail from '@/components/parts/molecules/MeasurementResultPatientDetail'
import Popup from '@/components/popups/Popup'
import dedent from 'dedent'
import moment from 'moment'
import { FocusTrap } from 'focus-trap-vue'
import { ValidationObserver } from 'vee-validate'
import { XMLParser } from 'fast-xml-parser'
import { mapGetters } from 'vuex'
import {
  toItemDefaultValues,
  useSettingDefaultValues
} from '@/utils/measurement_utils'

export default {
  name: 'IdexxMeasurementResultPopup',

  components: {
    AnnouncePopup,
    BaseFileInput,
    FocusTrap,
    MeasurementResultInputTable,
    MeasurementResultPatientDetail,
    Popup,
    ValidationObserver
  },

  mixins: [CheckPopupInputDifference],

  props: {
    layerNumber: { type: Number, default: 1 },
    patientId: { type: Number, default: 0 }
  },

  data() {
    return {
      alertPopup: {
        buttons: ['閉じる'],
        isOpened: false,
        layerNumber: this.layerNumber + 1,
        leftAlignMessage: '',
        message: '',
        title: '注意',
        type: 'alert'
      },
      confirmationPopup: {
        buttons: ['キャンセル', '閉じる'],
        isOpened: false,
        layerNumber: this.layerNumber + 1,
        message: '入力内容を保存せずに閉じようとしています。\nよろしいですか？',
        title: '確認',
        type: 'alert'
      },
      idexxInstrumentCodes: ['003', '015', '017', '014'],
      idexxInstruments: [],
      idexxQualifiers: ['', '?', '!', '-', '=', '<', '>', '#', '*', '~'],
      isCreatingMeasurement: false,
      isFileChanging: false,
      measurement: null,
      measurementResult: null,
      measurementResultItems: null,
      patient: null,
      patientSpecies: null,
      resultPopup: {
        buttons: ['閉じる'],
        isOpened: false,
        layerNumber: this.layerNumber + 2,
        message: '',
        title: '',
        type: ''
      },
      shouldCloseSelf: false,
      speciesAlertPopup: {
        buttons: ['キャンセル', '取込'],
        isOpened: false,
        layerNumber: this.layerNumber + 1,
        message: '',
        title: '確認',
        type: 'alert'
      },
      tmpMeasurementResult: null,
      tmpMeasurementResultItems: null,
      useSettingDefaultValues: false,
      xmlError: ''
    }
  },

  computed: {
    ...mapGetters({
      getLaboratoryDefaultValueSetting:
        'laboratoryDefaultValueSettings/getLaboratoryDefaultValueSetting',
      getLaboratoryItem: 'laboratoryItems/getLaboratoryItem',
      getLaboratoryItems: 'laboratoryItems/getLaboratoryItemsByInstrumentCode',
      getLaboratoryReferenceRange:
        'laboratoryReferenceRanges/getLaboratoryReferenceRange',
      getMaster: 'master/getData',
      getPatient: 'patients/getDataById',
      getSpecies: 'species/getDataById',
      instruments: 'master/getMasterLaboratoryInstruments',
      laboratorySpecies: 'master/getMasterLaboratorySpecies',
      lookOnlyFlg: 'auth/lookOnlyFlg'
    }),
    diffFlg() {
      // ファイルの有無で判定
      return !!this.fileName
    },
    fileInputLabel() {
      const idexxInstrumentsText = this.idexxInstruments
        .map(v => v.name.replace(/^IDEXX /i, ''))
        .join(', ')
      return `XMLファイルを選択 もしくは ドラッグ＆ドロップしてください\n※対応機器：${idexxInstrumentsText}`
    },
    fileName() {
      return this.measurement?.fileName ?? ''
    },
    isProcessing() {
      return this.isFileChanging || this.isCreatingMeasurement
    },
    isValidDataExceptInput() {
      return (
        this.isValidMeasurement &&
        this.isValidMeasurementResult &&
        this.isValidMeasurementResultItemsExceptInput
      )
    },
    isValidMeasurement() {
      return !!this.measurement?.fileName
    },
    isValidMeasurementResult() {
      const requiredKeys = ['masterLaboratorySpeciesCode', 'note']
      const requiredValues = [
        'date',
        'time',
        'masterLaboratoryInstrumentCode',
        'masterLaboratoryCategoryCode'
      ]
      return (
        !!this.measurementResult &&
        requiredKeys.every(k => Object.hasOwn(this.measurementResult, k)) &&
        requiredValues.every(v => !!this.measurementResult[v]) &&
        this.idexxInstrumentCodes.includes(
          this.measurementResult.masterLaboratoryInstrumentCode
        ) &&
        this.validCategoryCodes.includes(
          this.measurementResult.masterLaboratoryCategoryCode
        ) &&
        this.validSpeciesCodes.includes(
          this.measurementResult.masterLaboratorySpeciesCode
        )
      )
    },
    isValidMeasurementResultItemsExceptInput() {
      const requiredKeys = [
        'qualifier',
        'value',
        'unit',
        'referenceRangeLow',
        'referenceRangeHigh'
      ]
      const requiredValues = ['masterLaboratoryItemCode']
      return (
        !!this.measurementResultItems &&
        this.measurementResultItems.length > 0 &&
        this.measurementResultItems.every(
          item =>
            requiredKeys.every(k => Object.hasOwn(item, k)) &&
            requiredValues.every(v => !!item[v]) &&
            this.validItemCodes.includes(item.masterLaboratoryItemCode) &&
            this.idexxQualifiers.includes(item.qualifier)
        )
      )
    },
    patientSpeciesCode() {
      return this.patientSpecies?.masterLaboratorySpeciesCode ?? ''
    },
    resultInstrumentCode() {
      return this.measurementResult?.masterLaboratoryInstrumentCode ?? ''
    },
    resultSpeciesCode() {
      return this.measurementResult?.masterLaboratorySpeciesCode ?? ''
    },
    runDate() {
      return this.measurementResult?.date ?? ''
    },
    showInputTable() {
      return !!(this.measurementResult && this.measurementResultItems)
    },
    validCategoryCodes() {
      return this.getMaster('masterLaboratoryCategories').map(v => v.code)
    },
    validItemCodes() {
      return this.getLaboratoryItems(this.resultInstrumentCode).map(
        v => v.masterLaboratoryItemCode
      )
    },
    validSpeciesCodes() {
      const codes = this.laboratorySpecies.map(v => v.code)
      return [''].concat(codes)
    }
  },

  created() {
    this.setInstrumentValues()
    this.setPatientValues()
  },

  methods: {
    alertIfSpeciesDifferent() {
      if (this.patientSpeciesCode !== this.resultSpeciesCode) {
        const patientLaboratorySpeciesName = this.toLaboratorySpeciesName(
          this.patientSpeciesCode
        )
        const resultLaboratorySpeciesName = this.toLaboratorySpeciesName(
          this.resultSpeciesCode
        )
        const leftAlignMessage = dedent`
          患者の検査動物種と検査結果の動物種が一致していません。
          ・患者の検査動物種：${patientLaboratorySpeciesName}
          ・検査結果の動物種：${resultLaboratorySpeciesName}
        `
        this.openAlertPopup({ leftAlignMessage })
      }
    },
    async checkSpeciesThenCreate() {
      if (this.patientSpeciesCode === this.resultSpeciesCode) {
        await this.createMeasurement()
      } else {
        this.openSpeciesAlertPopup()
      }
    },
    closeAlertPopup() {
      this.alertPopup.isOpened = false
      this.alertPopup.leftAlignMessage = ''
      this.alertPopup.message = ''
    },
    closeConfirmationPopup() {
      this.confirmationPopup.isOpened = false
    },
    closeResultPopup() {
      this.resultPopup.isOpened = false
      if (this.shouldCloseSelf) {
        this.closeSelf()
      }
    },
    closeSelf() {
      this.$emit('close')
    },
    closeSpeciesAlertPopup() {
      this.speciesAlertPopup.isOpened = false
    },
    async createMeasurement() {
      this.isCreatingMeasurement = true
      const result = await this.$store.dispatch(
        'measurementResults/createIdexxMeasurement',
        {
          patientId: this.patientId,
          measurement: this.measurement,
          measurementResult: this.measurementResult,
          measurementResultItems: this.measurementResultItems
        }
      )
      if (result === 'success') {
        this.resultPopup = {
          ...this.resultPopup,
          isOpened: true,
          message: '取込に成功しました',
          title: '完了',
          type: 'success'
        }
        this.shouldCloseSelf = true
      } else {
        let message = '取込に失敗しました'
        if (result === 'no patient') {
          message = '取込に失敗しました。\n対象の患者は既に削除されています。'
          this.shouldCloseSelf = true
        }
        this.resultPopup = {
          ...this.resultPopup,
          isOpened: true,
          message,
          title: '失敗',
          type: 'failure'
        }
      }
      this.isCreatingMeasurement = false
    },
    getItemDefaultValues(itemCode) {
      // ※ファイル読込時に使用
      // patient 設定済み
      // patientSpecies 設定済み
      // tmpMeasurementResult 設定済み
      const laboratoryItem = this.getLaboratoryItem(
        this.tmpMeasurementResult.masterLaboratoryInstrumentCode,
        itemCode
      )
      const laboratoryItemRange = this.getLaboratoryReferenceRange(
        this.tmpMeasurementResult.masterLaboratoryInstrumentCode,
        itemCode,
        this.tmpMeasurementResult.masterLaboratorySpeciesCode,
        this.patient.birthday,
        this.tmpMeasurementResult.date
      )
      const { unit, low, high } = toItemDefaultValues(
        laboratoryItem,
        laboratoryItemRange
      )
      return { unit, low, high }
    },
    handleClose() {
      if (this.diffFlg) {
        this.confirmationPopup.isOpened = true
      } else {
        this.closeSelf()
      }
    },
    async inputFile(file) {
      if (!file || this.isFileChanging) return
      this.isFileChanging = true
      if (file.type !== 'text/xml') {
        const message = 'XMLファイルを選択してください'
        this.openAlertPopup({ message })
        this.isFileChanging = false
        return
      }
      const xmlDataStr = await this.readAsText(file)
      if (!xmlDataStr) {
        const message = 'ファイルの読み込みに失敗しました'
        this.openAlertPopup({ message })
        this.isFileChanging = false
        return
      }
      this.setDataFromXml(xmlDataStr)
      if (this.xmlError) {
        this.openAlertPopup({ leftAlignMessage: this.xmlError })
        this.xmlError = ''
      } else {
        this.measurement = { fileName: file.name, fileContent: xmlDataStr }
        this.measurementResult = this.tmpMeasurementResult
        this.measurementResultItems = this.tmpMeasurementResultItems
        this.alertIfSpeciesDifferent()
      }
      this.tmpMeasurementResult = null
      this.tmpMeasurementResultItems = null
      this.isFileChanging = false
    },
    inputMeasurementResult({ key, value }) {
      this.measurementResult[key] = value
    },
    inputMeasurementResultItem({ index, key, value }) {
      this.measurementResultItems[index][key] = value
    },
    inputMeasurementResultItems(measurementResultItems) {
      measurementResultItems.forEach((v, i) => {
        this.measurementResultItems.splice(i, 1, v)
      })
    },
    isIdexxInstrument(xmlInstrument) {
      // ※ファイル読込時に使用
      return this.idexxInstruments.some(
        v => v.fileInstrumentName === xmlInstrument
      )
    },
    isRegistrationDisabled(invalidInput) {
      return (
        !!this.lookOnlyFlg ||
        this.isProcessing ||
        !this.isValidDataExceptInput ||
        invalidInput
      )
    },
    makeItemCodeMap() {
      // ※ファイル読込時に使用
      // tmpMeasurementResult 設定済み
      const itemConverters = this.getMaster('masterLaboratoryItemConverters')
      const instrumentItemConverters = itemConverters.filter(
        v =>
          v.masterLaboratoryInstrumentCode ===
          this.tmpMeasurementResult.masterLaboratoryInstrumentCode
      )
      const itemCodeMap = new Map(
        instrumentItemConverters.map(v => [
          v.fileItemName,
          v.masterLaboratoryItemCode
        ])
      )
      return itemCodeMap
    },
    makeMeasurementResultItem(xmlAssayResult, itemCode) {
      // ※ファイル読込時に使用
      // tmpMeasurementResult 設定済み
      const xmlResultValue = xmlAssayResult['result_value']
      if (xmlResultValue === undefined) {
        this.xmlError = dedent`
          XMLファイルのデータ構造が正しくありません
          ・result_value タグがありません
        `
        return
      }
      const qualifier = xmlAssayResult['result_qualifier'] ?? ''
      if (!this.idexxQualifiers.includes(qualifier)) {
        this.xmlError = dedent`
          XMLファイルのデータ構造が正しくありません
          ・result_qualifier の値が不適切です
        `
        return
      }
      if (this.useSettingDefaultValues) {
        const { unit, low, high } = this.getItemDefaultValues(itemCode)
        return {
          masterLaboratoryItemCode: itemCode,
          qualifier,
          value: xmlResultValue,
          unit,
          referenceRangeLow: low,
          referenceRangeHigh: high
        }
      } else {
        const xmlUnit = xmlAssayResult['result_value_uom_cd']
        const unit = xmlUnit ?? ''
        const xmlReferenceRange = xmlAssayResult['assay_reference_range']
        const low = xmlReferenceRange?.low ?? ''
        const high = xmlReferenceRange?.high ?? ''
        return {
          masterLaboratoryItemCode: itemCode,
          qualifier,
          value: xmlResultValue,
          unit,
          referenceRangeLow: low,
          referenceRangeHigh: high
        }
      }
    },
    openAlertPopup({ leftAlignMessage, message }) {
      this.alertPopup = {
        ...this.alertPopup,
        isOpened: true,
        leftAlignMessage,
        message
      }
    },
    openSpeciesAlertPopup() {
      const message =
        '患者の検査動物種と検査結果の動物種が異なります。\n取込を行いますか？'
      this.speciesAlertPopup = {
        ...this.speciesAlertPopup,
        isOpened: true,
        message
      }
    },
    parseXml(xmlDataStr) {
      // ※ファイル読込時に使用
      const parserOptions = {
        ignoreAttributes: false, // 属性値の解析を有効化
        isArray: (tagName, tagPath, isLeafNode, isAttribute) => {
          const alwaysArray = [
            'message.body.result.results.assay_result',
            'message.body.result.instrument_notes.instrument_note'
          ]
          return alwaysArray.indexOf(tagPath) !== -1
        }, // 対象のタグを配列として解析
        parseTagValue: false // タグの値の解析を無効化
      }
      const parser = new XMLParser(parserOptions)
      const xmlJsonObject = parser.parse(xmlDataStr)
      return xmlJsonObject
    },
    async readAsText(file) {
      // ※ファイル読込時に使用
      const promise = new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.readAsText(file)
        reader.onload = () => {
          resolve(reader.result)
        }
        reader.onerror = () => {
          reject()
        }
      })
        .then(text => {
          return text
        })
        .catch(() => {
          return ''
        })
      const text = await promise
      return text
    },
    setDataFromXml(xmlDataStr) {
      // ※ファイル読込時に使用
      const xmlJsonObject = this.parseXml(xmlDataStr)
      const xmlResult = xmlJsonObject?.message?.body?.result
      if (!xmlResult) {
        this.xmlError = 'XMLファイルのデータ構造が正しくありません'
        return
      }
      const methods = [
        ['setTmpMeasurementResult', [xmlResult]],
        ['setTmpMeasurementResultItems', [xmlResult]]
      ]
      for (const method of methods) {
        const [name, args] = method
        this[name](...args)
        if (this.xmlError) {
          break
        }
      }
    },
    setInstrumentValues() {
      // created 時に使用
      this.idexxInstruments = this.instruments.filter(v =>
        this.idexxInstrumentCodes.includes(v.code)
      )
    },
    setPatientValues() {
      // created 時に使用
      const patient = this.getPatient(this.patientId)
      if (patient) {
        this.patient = patient
        const speciesId = patient.speciesId
        const patientSpecies = this.getSpecies(speciesId)
        if (patientSpecies) {
          this.patientSpecies = patientSpecies
        }
      }
    },
    setTmpMeasurementResult(xmlResult) {
      // ※ファイル読込時に使用
      const xmlInstrument = xmlResult['@_instrument']
      if (xmlInstrument === undefined) {
        this.xmlError = dedent`
          XMLファイルのデータ構造が正しくありません
          ・instrument 属性がありません
        `
        return
      }
      if (!this.isIdexxInstrument(xmlInstrument)) {
        const idexxInstrumentsText = this.idexxInstruments
          .map(v => `・${v.name.replace(/^IDEXX /i, '')}`)
          .join('\n')
        this.xmlError = dedent`
          未対応の検査機器です。
          対応している検査機器は以下の通りです。
          ${idexxInstrumentsText}
        `
        return
      }
      const instrument = this.idexxInstruments.find(
        v => v.fileInstrumentName === xmlInstrument
      )
      const instrumentCode = instrument.code
      const categoryCode = instrument.masterLaboratoryCategoryCode
      const xmlRunDt = xmlResult['run_dt']
      if (xmlRunDt === undefined) {
        this.xmlError = dedent`
          XMLファイルのデータ構造が正しくありません
          ・run_dt タグがありません
        `
        return
      }
      const dateTimeInput = 'MM/DD/YYYY hh:mm:ss.SSS A'
      const runDtMoment = moment(xmlRunDt, dateTimeInput, true)
      const isValidDt = runDtMoment.isValid()
      if (!isValidDt) {
        this.xmlError = dedent`
          XMLファイルのデータ構造が正しくありません
          ・run_dt タグの値が不適切です
        `
        return
      }
      const date = runDtMoment.format('YYYYMMDD')
      const time = runDtMoment.format('HHmm')
      const xmlPatientSpecies = xmlResult['patient']?.['@_patient_species']
      if (xmlPatientSpecies === undefined) {
        this.xmlError = dedent`
          XMLファイルのデータ構造が正しくありません
          ・patient_species 属性がありません
        `
        return
      }
      const speciesCode = this.toSpeciesCode(xmlPatientSpecies)
      const note = this.toNote(xmlResult)
      this.tmpMeasurementResult = {
        date,
        time,
        masterLaboratoryInstrumentCode: instrumentCode,
        masterLaboratoryCategoryCode: categoryCode,
        masterLaboratorySpeciesCode: speciesCode,
        note
      }
    },
    setTmpMeasurementResultItems(xmlResult) {
      // ※ファイル読込時に使用
      // tmpMeasurementResult 設定済み
      const xmlResults = xmlResult.results
      if (xmlResults === undefined) {
        this.xmlError = dedent`
          XMLファイルのデータ構造が正しくありません
          ・results タグがありません
        `
        return
      }
      const xmlAssayResults = xmlResults['assay_result']
      if (xmlAssayResults === undefined) {
        this.xmlError = dedent`
          XMLファイルのデータ構造が正しくありません
          ・assay_result タグがありません
        `
        return
      }

      // デフォルト値を設定するかの判定
      this.setUseSettingDefaultValues()

      // assay_result ごとに measurementResultItem を作成
      const itemCodeMap = this.makeItemCodeMap()
      const measurementResultItems = []
      for (const v of xmlAssayResults) {
        const assayName = v['@_assay_name']
        if (assayName === undefined) {
          this.xmlError = dedent`
            XMLファイルのデータ構造が正しくありません
            ・assay_name 属性がありません
          `
          break
        }
        const itemCode = itemCodeMap.get(assayName)
        if (!itemCode) {
          this.xmlError = dedent`
            未対応の検査項目が含まれています
            ・assay_name: ${assayName}
          `
          break
        }
        const measurementResultItem = this.makeMeasurementResultItem(
          v,
          itemCode
        )
        if (this.xmlError) {
          break
        }
        measurementResultItems.push(measurementResultItem)
      }
      this.tmpMeasurementResultItems = measurementResultItems
    },
    setUseSettingDefaultValues() {
      // ※ファイル読込時に使用
      // tmpMeasurementResult 設定済み
      const defaultValueSetting = this.getLaboratoryDefaultValueSetting(
        this.tmpMeasurementResult.masterLaboratoryInstrumentCode
      )
      this.useSettingDefaultValues = useSettingDefaultValues(
        defaultValueSetting,
        this.tmpMeasurementResult
      )
    },
    toLaboratorySpeciesName(speciesCode) {
      const laboratorySpecies = this.laboratorySpecies.find(
        v => v.code === speciesCode
      )
      return laboratorySpecies?.name ?? '未設定'
    },
    toNote(xmlResult) {
      // ※ファイル読込時に使用
      const instrumentNotes = xmlResult['instrument_notes']?.['instrument_note']
      const note = instrumentNotes?.join('\n') ?? ''
      return note
    },
    toSpeciesCode(xmlPatientSpecies) {
      // ※ファイル読込時に使用
      const speciesConverter = { CANINE: 'イヌ', FELINE: 'ネコ' }
      const speciesName = speciesConverter[xmlPatientSpecies] ?? ''
      const laboratorySpecies = this.laboratorySpecies.find(
        v => v.name === speciesName
      )
      if (laboratorySpecies) {
        const speciesCode = laboratorySpecies.code
        return speciesCode
      } else {
        return ''
      }
    }
  }
}
</script>

<style lang="scss" scoped>
$header-height: 43px;
$patient-detail-height: 70px;

.idexx-measurement-result-popup {
  position: absolute;
  top: 0;
  left: 0;
  .content {
    width: 790px;
    > .header {
      display: flex;
      justify-content: space-between;
      height: $header-height;
      position: sticky;
      top: 0;
      z-index: 200;
      background: #fff;
      > .title {
        text-align: left;
        font-size: 16px;
        font-weight: bold;
        color: #{$greyish-brown};
      }
    }
    > .input-form {
      > .file-input {
        display: flex;
        flex-direction: column;
        row-gap: 10px;
        width: 100%;
        font-size: 15px;
        margin-bottom: 20px;
        > .file-detail {
          display: flex;
          flex-direction: column;
          row-gap: 10px;
          width: 100%;
          > .file-name {
            display: flex;
            column-gap: 4px;
            > .icon > .check {
              padding-top: 4px;
              width: 16px;
              height: 16px;
            }
            > .text {
              line-height: 23px;
              overflow: hidden;
              overflow-wrap: break-word;
            }
          }
        }
      }
      > .patient {
        height: $patient-detail-height;
        position: sticky;
        top: $header-height;
        z-index: 100;
        background: #fff;
      }
      > .measurement-result {
        min-height: 200px;
      }
    }
  }
}
</style>
